import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { fetchJourneyValidate } from "@/lib/sacred-journey/sacred-journey-api";
import { DailyGuidance } from "./DailyGuidance";
import { Loader2, ArrowLeft, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { getEditorAuth } from "@/lib/sacred-library/sacred-library-api";

function SectionStatus({ section }) {
  if (!section) return null;
  return (
    <div className="flex items-center gap-2">
      {section.valid ? (
        <CheckCircle2 className="w-4 h-4 text-green-600" />
      ) : (
        <XCircle className="w-4 h-4 text-destructive" />
      )}
      {section.overridden && <Badge variant="secondary">overridden</Badge>}
    </div>
  );
}

export function JourneyValidationPage() {
  const { isAdmin } = useSacredAdmin();
  const [email, setEmail] = useState("user@example.com");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true);
    setMessage("");
    try {
      const data = await fetchJourneyValidate(email.trim() || undefined);
      setReport(data);
      if (data.error) setMessage(data.error);
    } catch (e) {
      setReport(null);
      setMessage(e?.response?.data?.detail || "Validation failed — admin login required");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin && getEditorAuth().token) load();
  }, [isAdmin]);

  if (!getEditorAuth().token) {
    return (
      <div className="min-h-screen py-24 px-4">
        <Link to="/admin/sacred" className="text-primary underline">
          Sign in
        </Link>{" "}
        as admin first.
      </div>
    );
  }

  if (!isAdmin) return <Navigate to="/admin/sacred" replace />;

  const breakdown = report?.mapping_breakdown;
  const tattva = breakdown?.tattva_influences;
  const overrides = report?.override_effects ?? {};

  return (
    <div className="min-h-screen py-24 px-4" data-testid="journey-validation-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/sacred">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Link>
          </Button>
          <h1 className="font-heading text-2xl">Journey Validation</h1>
          {report && (
            <Badge variant={report.valid ? "default" : "destructive"}>
              {report.valid ? "PASS" : "FAIL"} · {report.summary?.passed}/{report.summary?.passed + report.summary?.failed}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Console:{" "}
          <code className="text-xs bg-muted px-1 rounded">validateJourney(&quot;{email}&quot;)</code>
        </p>

        <Card>
          <CardContent className="pt-6 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[220px]">
              <Label>User email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <Button type="button" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Validate
            </Button>
          </CardContent>
        </Card>

        {message && <p className="text-sm text-muted-foreground">{message}</p>}

        {report && !report.error && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {["archetype", "karmic_gate", "adhikara", "pathway", "daily_guidance"].map((key) => {
                const section = report[key];
                return (
                  <Card key={key}>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                      <CardTitle className="text-sm capitalize">{key.replace(/_/g, " ")}</CardTitle>
                      <SectionStatus section={section} />
                    </CardHeader>
                    <CardContent className="text-xs space-y-1">
                      {section.computed?.id && <p>ID: <strong>{section.computed.id}</strong></p>}
                      {section.errors?.map((e) => (
                        <p key={e} className="text-destructive">{e}</p>
                      ))}
                      {section.warnings?.map((w) => (
                        <p key={w} className="text-amber-700">{w}</p>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {report.daily_guidance?.computed?.date && (
              <DailyGuidance guidance={report.daily_guidance.computed} date={report.date} />
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Mapping breakdown</CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-3">
                <div>
                  <p className="font-semibold">Top archetype scores</p>
                  <pre className="bg-muted p-2 rounded overflow-auto max-h-32">
                    {JSON.stringify(
                      Object.entries(breakdown?.archetype_scores ?? {})
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 6),
                      null,
                      2
                    )}
                  </pre>
                </div>
                <div>
                  <p className="font-semibold">Gate scores</p>
                  <pre className="bg-muted p-2 rounded overflow-auto max-h-32">
                    {JSON.stringify(
                      Object.entries(breakdown?.gate_scores ?? {})
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 6),
                      null,
                      2
                    )}
                  </pre>
                </div>
                <div>
                  <p className="font-semibold">Pathway scores</p>
                  <pre className="bg-muted p-2 rounded">
                    {JSON.stringify(breakdown?.pathway_scores, null, 2)}
                  </pre>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Tattva influences</CardTitle>
              </CardHeader>
              <CardContent className="text-xs grid sm:grid-cols-2 gap-2">
                <p>Elements: {(tattva?.elements ?? []).join(", ") || "—"}</p>
                <p>Guṇas: {(tattva?.gunas ?? []).join(", ") || "—"}</p>
                <p>Planets: {(tattva?.planets ?? []).join(", ") || "—"}</p>
                <p>Psychology: {(tattva?.psychology ?? []).join(", ") || "—"}</p>
                <p>Chakras: {(tattva?.chakras ?? []).join(", ") || "—"}</p>
                <p>Mantras: {(tattva?.mantras ?? []).join(", ") || "—"}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Sādhana influences</CardTitle>
              </CardHeader>
              <CardContent className="text-xs">
                {(breakdown?.sadhana_influences ?? []).length === 0 && (
                  <p className="text-muted-foreground">No preferred sādhanā on sacred profile.</p>
                )}
                <ul className="list-disc pl-4">
                  {(breakdown?.sadhana_influences ?? []).map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
                <p className="mt-2 text-muted-foreground">
                  Adhikāra factors: log days {breakdown?.adhikara_factors?.logDays}, streak{" "}
                  {breakdown?.adhikara_factors?.streak}, tattva profile{" "}
                  {breakdown?.adhikara_factors?.hasTattva ? "yes" : "no"}
                </p>
              </CardContent>
            </Card>

            {Object.keys(overrides).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Override effects</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-2">
                  {Object.entries(overrides).map(([key, eff]) => (
                    <p key={key}>
                      <strong>{key}</strong>: {eff.natural} → {eff.applied} ({eff.field})
                    </p>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
